import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Search, Book, FileText, ExternalLink } from 'lucide-react';

// Nextra-style docs layout with sidebar navigation
const DocsLayout = ({ docsConfig, children }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  const [activePage, setActivePage] = useState(docsConfig.defaultPage || null);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Filter docs based on search
  const filterDocs = (items) => {
    if (!searchTerm) return items;
    return items.filter(item => {
      const matchesTitle = item.title.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesChildren = item.children?.some(child => 
        child.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
      return matchesTitle || matchesChildren;
    });
  };

  const filteredItems = filterDocs(docsConfig.items);

  // Find active content
  const findActiveContent = () => {
    for (const section of docsConfig.items) {
      if (section.id === activePage) return section;
      if (section.children) {
        const child = section.children.find(c => c.id === activePage);
        if (child) return child;
      }
    }
    return null;
  };

  const activeContent = findActiveContent();

  return (
    <div className="flex h-full">
      {/* Docs Sidebar */}
      <div className="w-64 flex-shrink-0 border-r border-dark-border bg-dark-card/50 overflow-y-auto"
           style={{ background: 'rgba(20, 20, 20, 0.8)' }}>
        
        {/* Search */}
        <div className="p-4 border-b border-dark-border">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              placeholder="Search docs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-dark-border/40 border border-dark-border rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-cyan-accent/50"
            />
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-3">
          {filteredItems.map((section) => (
            <div key={section.id} className="mb-1">
              {section.children ? (
                // Section with children (expandable)
                <>
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-dark-border/30 rounded-lg transition-colors"
                  >
                    <div className="flex items-center space-x-2">
                      {section.icon && <section.icon size={16} className="text-gray-500" />}
                      <span>{section.title}</span>
                    </div>
                    {expandedSections[section.id] ? (
                      <ChevronDown size={16} className="text-gray-500" />
                    ) : (
                      <ChevronRight size={16} className="text-gray-500" />
                    )}
                  </button>
                  
                  {expandedSections[section.id] && (
                    <div className="ml-4 mt-1 space-y-1 border-l border-dark-border/50 pl-3">
                      {section.children.map((child) => (
                        <button
                          key={child.id}
                          onClick={() => setActivePage(child.id)}
                          className={`w-full text-left px-3 py-1.5 text-sm rounded-lg transition-colors ${
                            activePage === child.id
                              ? 'text-cyan-accent bg-cyan-accent/10 border-l-2 border-cyan-accent -ml-[13px] pl-[23px]'
                              : 'text-gray-400 hover:text-white hover:bg-dark-border/20'
                          }`}
                        >
                          {child.title}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                // Single page (no children)
                <button
                  onClick={() => setActivePage(section.id)}
                  className={`w-full flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activePage === section.id
                      ? 'text-cyan-accent bg-cyan-accent/10'
                      : 'text-gray-300 hover:text-white hover:bg-dark-border/30'
                  }`}
                >
                  {section.icon && <section.icon size={16} className={activePage === section.id ? 'text-cyan-accent' : 'text-gray-500'} />}
                  <span>{section.title}</span>
                </button>
              )}
            </div>
          ))}
        </nav>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto">
        {activeContent ? (
          <div className="max-w-4xl mx-auto p-8">
            {/* Breadcrumb */}
            <div className="flex items-center space-x-2 text-sm text-gray-500 mb-6">
              <span>Docs</span>
              <ChevronRight size={14} />
              <span className="text-cyan-accent">{activeContent.title}</span>
            </div>

            {/* Page Title */}
            <h1 className="text-3xl font-bold text-white mb-2"
                style={{ textShadow: '0 0 20px rgba(255,255,255,0.1)' }}>
              {activeContent.title}
            </h1>
            
            {activeContent.description && (
              <p className="text-gray-400 text-lg mb-8">{activeContent.description}</p>
            )}

            {/* Content */}
            <div className="docs-content">
              {activeContent.content}
            </div>
          </div>
        ) : (
          // Welcome/Index page
          <div className="max-w-4xl mx-auto p-8">
            <div className="text-center py-12">
              <Book size={48} className="mx-auto text-cyan-accent mb-4" style={{ filter: 'drop-shadow(0 0 20px currentColor)' }} />
              <h1 className="text-3xl font-bold text-white mb-4"
                  style={{ textShadow: '0 0 20px rgba(255,255,255,0.1)' }}>
                {docsConfig.title}
              </h1>
              <p className="text-gray-400 mb-8">{docsConfig.description}</p>
              
              {/* Quick Links */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                {docsConfig.items.slice(0, 4).map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      if (item.children) {
                        setExpandedSections(prev => ({ ...prev, [item.id]: true }));
                        setActivePage(item.children[0].id);
                      } else {
                        setActivePage(item.id);
                      }
                    }}
                    className="p-4 bg-dark-card border border-dark-border rounded-lg hover:border-cyan-accent/40 transition-colors group"
                  >
                    <div className="flex items-center space-x-3 mb-2">
                      {item.icon && <item.icon size={20} className="text-cyan-accent" />}
                      <span className="font-semibold text-white group-hover:text-cyan-accent transition-colors">
                        {item.title}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">{item.description || `${item.children?.length || 0} articles`}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocsLayout;
